import { Injectable, OnDestroy } from '@angular/core';
import { CategoriesMenuNode, DesktopCategoriesMenuNode } from '@main/categories-menu/const';
import { CategoriesMenuService } from '@main/categories-menu/data-access';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { debounceTime, filter, map } from 'rxjs/operators';

@Injectable()
export class DesktopCategoriesMenuComponentService implements OnDestroy {
  private _nodes: DesktopCategoriesMenuNode[] = [];
  private _nodes$ = new BehaviorSubject<DesktopCategoriesMenuNode[]>(this._nodes);

  private _changeNodeListDebounceSubject: BehaviorSubject<{
    nodeId: string;
    level: number;
    hasChildren: boolean;
  }> = new BehaviorSubject(null);

  private subscription = new Subscription();

  constructor(private categoriesMenuService: CategoriesMenuService) {
    this.subscription.add(
      this._changeNodeListDebounceSubject
        .pipe(filter(Boolean), debounceTime(200))
        .subscribe(({ nodeId, level, hasChildren }) => {
          this.changeNodesList(nodeId, level, hasChildren);
        }),
    );
  }

  getNodes(): Observable<DesktopCategoriesMenuNode[]> {
    return this._nodes$.asObservable();
  }

  isInNodesList(nodeId: string): boolean {
    return this._nodes.some((n: DesktopCategoriesMenuNode) => n.nodeId === nodeId);
  }

  changeNodesListWithDebounce(nodeId: string, level: number, hasChildren: boolean): void {
    this._changeNodeListDebounceSubject.next({ nodeId, level, hasChildren });
  }

  changeNodesList(nodeId: string, level: number, hasChildren: boolean): void {
    if (!hasChildren) {
      if (level < this._nodes.length) {
        this._nodes.splice(level);
        this._nodes$.next(this._nodes);
      }

      return;
    }

    const node = this._createNode(nodeId, level);

    if (level <= this._nodes.length) {
      this._nodes.splice(level - 1, Infinity, node);
      this._nodes$.next(this._nodes);

      return;
    }

    if (level > this._nodes.length) {
      this._nodes.push(node);
      this._nodes$.next(this._nodes);

      return;
    }
  }

  resetNodesList(): void {
    if (this._nodes.length > 1) {
      this._nodes.splice(1);
      this._nodes$.next(this._nodes);
    }
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  private _createNode(nodeId: string, level: number): DesktopCategoriesMenuNode {
    return {
      nodeId,
      level,
      items$: this.categoriesMenuService
        .getNode({
          nodeId,
          level: 1,
        })
        .pipe(map((n: CategoriesMenuNode) => n?.items)),
    };
  }
}
